<script setup lang="ts">
import { trimmedMaybe } from "../lib/trimmedString";
import {
  atIndexMaybe,
  fromMaybe,
  just,
  matchMaybe,
  nothing,
  isJust,
  isNothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import { createTimeoutHandle } from "../lib/timeoutHandle";
import { computed, nextTick, ref, watch } from "vue";
import { ask } from "@tauri-apps/plugin-dialog";
import {
  commentAnchorsOf,
  type CommentAnchorSummary,
} from "../editor/commentDoc";
import {
  readCommentStore,
  type CommentMessage,
  type CommentStore,
  type CommentThread,
} from "../editor/commentStore";
import { displayCommentAuthor } from "../editor/commentAuthor";
import { ensureCommentAuthorLabels } from "../editor/commentAuthorLabels";
import { getCloudSession } from "../lib/cloudAuth";
import { isTauri } from "../lib/runtime";
import { IconCollapse } from "./ribbonIcons";
import ZwButton from "./ui/ZwButton.vue";
import ZwTextarea from "./ui/ZwTextarea.vue";

export type ThreadPlacement = {
  id: string;
  top: number;
  left: number;
  width: number;
};

const props = defineProps<{
  doc: string;
  activeId: Maybe<string>;
  openId: Maybe<string>;
  hoveredId: Maybe<string>;
  placements: ThreadPlacement[];
}>();

function trimMaybeString(value: Maybe<string>): Maybe<string> {
  return matchMaybe(value, {
    nothing: () => nothing,
    just: (raw) => trimmedMaybe(raw),
  });
}

const emit = defineEmits<{
  select: [id: string];
  collapse: [id: string];
  /** Kept for threaded data model; UI is single-input for now. */
  reply: [threadId: string, body: string];
  updateMessage: [threadId: string, messageId: string, body: string];
  deleteMessage: [threadId: string, messageId: string];
  toggleResolved: [id: string];
  remove: [id: string];
  layout: [];
}>();

const messageDrafts = ref<Record<string, string>>({});
const cardEls = ref<Record<string, Maybe<HTMLElement>>>({});
/** Focus the card input once it exists (placements/layout may lag). */
const pendingFocusId = ref<Maybe<string>>(nothing);
let focusAttempts = 0;
let focusRaf = 0;

const store = computed<CommentStore>(() => readCommentStore(props.doc));
const anchors = computed<CommentAnchorSummary[]>(() =>
  commentAnchorsOf(props.doc),
);

const threads = computed(() => {
  const byId = new Map(props.placements.map((p) => [p.id, p]));
  return anchors.value
    .map((anchor) => {
      const threadM = toMaybe(store.value[anchor.id]);
      const placementM = toMaybe(byId.get(anchor.id));
      if (isNothing(threadM) || isNothing(placementM)) return [];
      const thread = threadM.value;
      const primaryM = atIndexMaybe(thread.messages, 0);
      return [
        {
          anchor,
          thread,
          placement: placementM.value,
          primary: primaryM,
        },
      ];
    })
    .flat();
});

watch(
  threads,
  (list) => {
    const next = { ...messageDrafts.value };
    const liveIds = new Set<string>();
    for (const { thread } of list) {
      const primaryM = primaryMessage(thread);
      if (isNothing(primaryM)) continue;
      liveIds.add(primaryM.value.id);
      if (primaryM.value.id in next === false) {
        next[primaryM.value.id] = primaryM.value.body;
      }
    }
    for (const id of Object.keys(next)) {
      if (liveIds.has(id) === false) delete next[id];
    }
    messageDrafts.value = next;
    if (isJust(pendingFocusId.value)) scheduleFocusAttempt();
  },
  { immediate: true },
);

function cancelFocusAttempts(): void {
  if (focusRaf !== 0) {
    cancelAnimationFrame(focusRaf);
    focusRaf = 0;
  }
  focusAttempts = 0;
}

function requestFocus(id: string): void {
  pendingFocusId.value = just(id);
  cancelFocusAttempts();
  scheduleFocusAttempt();
}

function scheduleFocusAttempt(): void {
  if (focusRaf !== 0) cancelAnimationFrame(focusRaf);
  focusRaf = requestAnimationFrame(() => {
    focusRaf = 0;
    void tryFocusPending();
  });
}

async function tryFocusPending(): Promise<void> {
  const id = trimmedMaybe(fromMaybe(pendingFocusId.value));
  if (isNothing(id)) return;

  await nextTick();
  const cardM = cardEls.value[id.value] ?? nothing;
  const thread = toMaybe(store.value[id.value]);
  if (isNothing(thread) || thread.value.resolved === true) {
    if (focusAttempts++ < 24) scheduleFocusAttempt();
    else cancelFocusAttempts();
    return;
  }

  const input = isJust(cardM)
    ? toMaybe(cardM.value.querySelector("[data-comment-body]") as HTMLElement)
    : nothing;

  if (isNothing(input)) {
    if (focusAttempts++ < 24) scheduleFocusAttempt();
    else {
      pendingFocusId.value = nothing;
      cancelFocusAttempts();
    }
    return;
  }

  // Already typing in this card. Don't yank caret around.
  if (
    isJust(cardM) &&
    cardM.value.contains(document.activeElement) === true &&
    document.activeElement === input.value
  ) {
    pendingFocusId.value = nothing;
    cancelFocusAttempts();
    return;
  }

  input.value.focus({ preventScroll: true });
  if (document.activeElement === input.value) {
    pendingFocusId.value = nothing;
    cancelFocusAttempts();
    return;
  }

  if (focusAttempts++ < 24) scheduleFocusAttempt();
  else {
    pendingFocusId.value = nothing;
    cancelFocusAttempts();
  }
}

watch(
  () => props.activeId,
  (id, prev) => {
    const next = trimMaybeString(id);
    const prevTrimmed = trimMaybeString(prev);
    if (isNothing(next)) return;
    if (isJust(prevTrimmed) && prevTrimmed.value === next.value) return;
    requestFocus(next.value);
  },
);

watch(
  () => props.openId,
  async (id, prev, onCleanup) => {
    // Persist (or drop empty) the thread we're leaving. Drafts aren't in the doc yet.
    const prevId = trimMaybeString(prev);
    const openId = trimMaybeString(id);
    if (
      isJust(prevId) &&
      (isNothing(openId) || openId.value !== prevId.value)
    ) {
      void finalizeLeavingThread(prevId.value);
    }

    if (isNothing(openId)) return;
    await nextTick();
    const card = cardEls.value[openId.value] ?? nothing;
    if (isJust(card)) {
      for (const el of card.value.querySelectorAll("[data-comment-body]")) {
        const area = el as HTMLElement;
        area.style.height = "0px";
        area.style.height = `${Math.max(area.scrollHeight, 1)}px`;
      }
      emit("layout");
    }
    if (isNothing(prevId) || prevId.value !== openId.value) {
      requestFocus(openId.value);
    }

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (target instanceof Node === false) return;
      const openCard = cardEls.value[openId.value] ?? nothing;
      if (isJust(openCard) && openCard.value.contains(target) === true) return;
      const el = toMaybe(
        target instanceof Element ? target : target.parentElement,
      );
      // Let in-editor comment marks open/switch threads instead of only collapsing.
      if (isJust(el) && el.value.closest(".zw-comment-text") !== null) return;
      void collapseThread(openId.value);
    };

    const timer = createTimeoutHandle();
    timer.schedule(0, () => {
      window.addEventListener("pointerdown", onPointerDown, true);
    });
    onCleanup(() => {
      timer.clear();
      window.removeEventListener("pointerdown", onPointerDown, true);
    });
  },
);

function setCardEl(id: string, el: unknown): void {
  const nodeM =
    el instanceof HTMLElement ? just(el) : (nothing as Maybe<HTMLElement>);
  cardEls.value[id] = nodeM;
  if (
    isJust(nodeM) &&
    isJust(pendingFocusId.value) &&
    pendingFocusId.value.value === id
  ) {
    scheduleFocusAttempt();
  }
}

function onLayout(): void {
  emit("layout");
}

function primaryMessage(thread: CommentThread): Maybe<CommentMessage> {
  return atIndexMaybe(thread.messages, 0);
}

const viewerEmail = computed(() => getCloudSession()?.email ?? null);
const viewerUserId = computed(() => getCloudSession()?.userId ?? null);
const labelsByUserId = ref<Record<string, string>>({});

const authorIdsNeedingLabels = computed(() => {
  const ids = new Set<string>();
  for (const thread of Object.values(store.value)) {
    for (const message of thread.messages) {
      const id = message.authorId?.trim() ?? "";
      if (id !== "") ids.add(id);
    }
  }
  return [...ids];
});

watch(
  authorIdsNeedingLabels,
  async (ids) => {
    if (ids.length === 0) return;
    const labels = await ensureCommentAuthorLabels(ids);
    if (Object.keys(labels).length === 0) return;
    labelsByUserId.value = { ...labelsByUserId.value, ...labels };
  },
  { immediate: true },
);

function authorLabel(message: Maybe<CommentMessage>): string {
  return displayCommentAuthor(fromMaybe(message), {
    currentUserId: viewerUserId.value,
    currentEmail: viewerEmail.value,
    labelsByUserId: labelsByUserId.value,
  });
}

function summary(thread: CommentThread): string {
  const primary = primaryMessage(thread);
  return matchMaybe(primary, {
    nothing: () => "Empty comment",
    just: (message) => {
      const draft =
        message.id in messageDrafts.value
          ? (messageDrafts.value[message.id] ?? message.body)
          : message.body;
      const body = draft.trim();
      return body !== "" ? body : "Empty comment";
    },
  });
}

function previewSnippet(thread: CommentThread): string {
  return summary(thread);
}

function isOpen(id: string): boolean {
  return isJust(props.openId) && props.openId.value === id;
}

function onMessageDraft(messageId: string, value: string): void {
  messageDrafts.value = {
    ...messageDrafts.value,
    [messageId]: value,
  };
}

function commitMessage(threadId: string, messageId: string): void {
  if (messageId in messageDrafts.value === false) return;
  const body = messageDrafts.value[messageId] ?? "";
  const current = store.value[threadId]?.messages.find(
    (m) => m.id === messageId,
  )?.body;
  if (body === current) return;
  emit("updateMessage", threadId, messageId, body);
}

function messageBody(message: CommentMessage): string {
  if (message.id in messageDrafts.value) {
    return messageDrafts.value[message.id] ?? message.body;
  }
  return message.body;
}

function threadHasContent(thread: CommentThread): boolean {
  const primary = primaryMessage(thread);
  if (isNothing(primary)) return false;
  return messageBody(primary.value).trim().length > 0;
}

/** Commit draft text, or remove the thread if still empty (with confirm). */
async function finalizeLeavingThread(id: string): Promise<void> {
  const thread = toMaybe(store.value[id]);
  if (isNothing(thread)) return;
  if (threadHasContent(thread.value) === false) {
    await confirmRemove(id, "Discard empty comment?");
    return;
  }
  const primary = primaryMessage(thread.value);
  if (isJust(primary)) commitMessage(id, primary.value.id);
}

/** Collapse, or delete the thread if nothing was written. */
async function collapseThread(id: string): Promise<void> {
  const thread = toMaybe(store.value[id]);
  if (isNothing(thread) || threadHasContent(thread.value) === false) {
    await confirmRemove(id, "Discard empty comment?");
    return;
  }
  const primary = primaryMessage(thread.value);
  if (isJust(primary)) commitMessage(id, primary.value.id);
  emit("collapse", id);
}

async function confirmRemove(id: string, message: string): Promise<void> {
  if (isTauri()) {
    const ok = await ask(message, {
      title: "Comment",
      kind: "warning",
    });
    if (ok === false) return;
  } else if (window.confirm(message) === false) {
    return;
  }
  emit("remove", id);
}

async function onDeleteClick(id: string): Promise<void> {
  await confirmRemove(id, "Delete this comment?");
}

function onToggleResolved(id: string): void {
  const wasResolved = store.value[id]?.resolved === true;
  emit("toggleResolved", id);
  if (wasResolved) {
    // Back to an open thread — focus the body once the store updates.
    requestFocus(id);
  } else {
    // Resolved: fold back to the gutter preview.
    void collapseThread(id);
  }
}
</script>

<template>
  <div
    class="pointer-events-none absolute inset-0 z-10 overflow-hidden"
    role="complementary"
    aria-label="Comments"
    style="font-family: var(--font-ui)"
  >
    <article
      v-for="{ anchor, thread, placement, primary } in threads"
      :key="anchor.id"
      :ref="(el) => setCardEl(anchor.id, el)"
      :data-margin-comment="anchor.id"
      class="absolute transition duration-200 ease-[cubic-bezier(0.25,1,0.5,1)]"
      :class="
        isOpen(anchor.id)
          ? 'pointer-events-auto zw-scroll max-h-[min(24rem,70%)] overflow-y-auto rounded-md border p-3'
          : 'pointer-events-none px-1.5 py-1'
      "
      :style="
        isOpen(anchor.id)
          ? {
              top: `${placement.top}px`,
              left: `${placement.left}px`,
              width: `${placement.width}px`,
              borderColor:
                isJust(activeId) && activeId.value === anchor.id
                  ? 'color-mix(in oklch, var(--color-zw-amber) 55%, transparent)'
                  : 'color-mix(in oklch, var(--line) 90%, transparent)',
              // Opaque: an open card can sit over the manuscript.
              background: 'var(--bg-raised)',
              boxShadow:
                '0 1px 0 color-mix(in oklch, var(--text-strong) 4%, transparent)',
            }
          : {
              top: `${placement.top}px`,
              left: `${placement.left}px`,
              width: `${placement.width}px`,
              background: 'transparent',
            }
      "
    >
      <button
        v-if="isOpen(anchor.id) === false"
        type="button"
        class="pointer-events-auto block w-full text-left text-[13px] leading-snug transition-colors"
        :class="{ 'line-through': thread.resolved }"
        :style="{
          color:
            isJust(hoveredId) && hoveredId.value === anchor.id
              ? 'var(--text)'
              : 'var(--text-muted)',
          fontFamily: 'var(--font-prose)',
        }"
        :title="previewSnippet(thread)"
        @mousedown="emit('select', anchor.id)"
      >
        <span
          class="mb-0.5 block text-[10px] font-medium tracking-wide"
          style="color: var(--text-muted)"
        >
          {{ authorLabel(primary) }}
        </span>
        <span class="whitespace-pre-wrap break-words">{{
          previewSnippet(thread)
        }}</span>
      </button>

      <template v-else>
        <div class="mb-2 flex items-start gap-1">
          <button
            type="button"
            class="min-w-0 flex-1 truncate text-left text-[11px] transition"
            style="color: var(--text-muted)"
            :title="anchor.previewText"
            @click="emit('select', anchor.id)"
          >
            {{ anchor.previewText }}
          </button>
          <button
            type="button"
            class="zw-icon-btn shrink-0 rounded p-1"
            title="Collapse comment"
            aria-label="Collapse comment"
            @mousedown.stop
            @click.stop="collapseThread(anchor.id)"
          >
            <IconCollapse class="size-3.5" />
          </button>
        </div>

        <template v-if="primary.tag === 'just'">
          <div
            class="mb-1 text-[11px] font-medium"
            style="color: var(--text-muted)"
          >
            {{ authorLabel(primary) }}
          </div>
          <ZwTextarea
            :id="nothing"
            :model-value="messageDrafts[primary.value.id] ?? primary.value.body"
            autosize
            :rows="1"
            data-comment-body
            class="comment-body"
            :readonly="thread.resolved"
            placeholder="Write a comment…"
            :label="nothing"
            @update:model-value="onMessageDraft(primary.value.id, $event)"
            @autosize="onLayout"
            @focus="emit('select', anchor.id)"
            @blur="commitMessage(anchor.id, primary.value.id)"
          />

          <div class="mt-2 flex items-center gap-1">
            <ZwButton size="sm" @click="onToggleResolved(anchor.id)">
              {{ thread.resolved === true ? "Reopen" : "Resolve" }}
            </ZwButton>
            <ZwButton
              size="sm"
              variant="danger"
              @click="onDeleteClick(anchor.id)"
            >
              Delete
            </ZwButton>
          </div>
        </template>
      </template>
    </article>
  </div>
</template>

<style scoped>
:deep(.comment-body) {
  font-family: var(--font-prose);
  font-size: 0.875rem;
  line-height: 1.35;
  background: color-mix(in oklch, var(--bg) 80%, transparent);
}
</style>
